
import React, { Component } from 'react'
import { Card, notification, Space, Upload, Button, Empty, Collapse, Table, Typography } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import Http from '../../../utils/http'
import Slider from 'react-slick'
import './tabs.css'

const { Panel } = Collapse
const { Title } = Typography


class WeightBalance extends Component {
  constructor(props) {
    super(props)
    this.state = {
      data: [],
      env: [],
      fuel: [],
      tab: 'oew',
      activeAircraftKeys: []
    }
  }

  componentDidMount() {
    this.loadInitialData()
  }

  loadInitialData = async () => {
    try {
      const [data, env, fuel] = await Promise.all([
        this.getData('/w_b/oew'),
        this.getData('/w_b/env'),
        this.getData('/w_b/fuel')
      ])

      this.setState({
        data,
        env,
        fuel
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching initial weight and balance data:', error)
    }
  }

  getData = async (endpoint) => {
    const response = await Http.get(endpoint)
    return response?.data?.data || []
  }

  fetchData = async () => {
    try {
      const data = await this.getData('/w_b/oew')
      this.setState({ data })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching data:', error)
    }
  }

  fetchEnv = async () => {
    try {
      const env = await this.getData('/w_b/env')
      this.setState({ env })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching data:', error)
    }
  }

  fetchFuel = async () => {
    try {
      const fuel = await this.getData('/w_b/fuel')
      this.setState({ fuel })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching data:', error)
    }
  }

  handleUpload = async (endpoint, file) => {
    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await Http.post(
        endpoint,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'station': this.state.station
          }
        }
      )

      notification.destroy()

      if (response?.status === 200) {
        notification.success({
          message: 'Success',
          description: response?.data?.message,
        })
        await this.fetchData()
      } else {
        notification.error({
          message: 'Failed',
          description: response?.data?.message,
        })
      }
    } catch (err) {
      notification.destroy()
      notification.error({
        message: 'Failed',
        description: err?.response?.data?.message || 'Something went wrong',
      })
    }

    return false
  }

  getAircraftPanelKey = (item, index) => `${item?.type || 'type'}-${index}`

  getAircraftPanelKeys = (data = []) => data.map((item, index) => this.getAircraftPanelKey(item, index))

  handleAircraftAccordionChange = (keys) => {
    this.setState({ activeAircraftKeys: Array.isArray(keys) ? keys : keys ? [keys] : [] })
  }

  toggleAllAircraft = () => {
    const allKeys = this.getAircraftPanelKeys(this.state.data)
    const isAllExpanded = allKeys.length > 0 && allKeys.every((key) => this.state.activeAircraftKeys.includes(key))
    this.setState({ activeAircraftKeys: isAllExpanded ? [] : allKeys })
  }

  getColumnTitle = (key) => key.charAt(0).toUpperCase() + key.slice(1).split('_').join(' ')

  getExtraColumnNames = (rows = []) => {
    const hiddenColumns = ['ship', 'dom_oew_adj_wt', 'intl_oew_adj_wt', 'config']
    const extraColumns = []

    rows.forEach((value) => {
      Object.keys(value || {}).forEach((key) => {
        if (!hiddenColumns.includes(key) && !extraColumns.includes(key)) {
          extraColumns.push(key)
        }
      })
    })

    return extraColumns
  }

  getOewColumns = (extraColumns = []) => {
    const baseColumns = [
      {
        title: 'Ship',
        dataIndex: 'ship',
        key: 'ship',
        className: 'wb-col-ship',
        width: 90,
        render: (value) => value || '-'
      },
      {
        title: 'Dom OEW Adj Wt',
        dataIndex: 'dom_oew_adj_wt',
        key: 'dom_oew_adj_wt',
        className: 'wb-col-dom',
        width: 150,
        render: (value) => value || '-'
      },
      {
        title: 'Intl OEW Adj Wt',
        dataIndex: 'intl_oew_adj_wt',
        key: 'intl_oew_adj_wt',
        className: 'wb-col-intl',
        width: 150,
        render: (value) => value || '-'
      },
      {
        title: 'Config',
        dataIndex: 'config',
        key: 'config',
        className: 'wb-col-config',
        width: 120,
        render: (value) => <span className='wb-config-pill'>{value || '-'}</span>
      }
    ]

    const dynamicColumns = extraColumns.map((column) => ({
      title: this.getColumnTitle(column),
      dataIndex: column,
      key: column,
      className: 'wb-col-extra',
      width: 120,
      render: (value) => value || '-'
    }))

    return [...baseColumns, ...dynamicColumns]
  }

  oew = () => {
    const { data, activeAircraftKeys } = this.state
    const allPanelKeys = this.getAircraftPanelKeys(data)
    const isAllExpanded = allPanelKeys.length > 0 && activeAircraftKeys.length === allPanelKeys.length

    return (
      <div className='wb-panel'>
        <div className='wb-panel-head'>
          <Title level={5} className='mb-0'>Operating Weight (OEW)</Title>
          <Space direction='horizontal' size={0} className='ml-auto d-flex'>
            <Upload accept='.xlsx,.xls' showUploadList={false} beforeUpload={(file) => this.handleUpload('/w_b/oew', file)}>
              <Button type='link' className='link-btn-dod uld-upload wb-upload-btn' icon={<UploadOutlined style={{ fontSize: 16 }} />}>Upload</Button>
            </Upload>
            <Button className='custom-button' onClick={this.toggleAllAircraft}>
              {isAllExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </Space>
        </div>

        {data.length === 0 ? (
          <Card className='wb-type-card'>
            <Empty description='No OEW records found' />
          </Card>
        ) : (
          <div className='wb-card'>
            <Collapse
              activeKey={activeAircraftKeys}
              onChange={this.handleAircraftAccordionChange}
              className='wb-accordion'
              bordered={false}
            >
              {data.map((item, index) => {
                const oewRows = item?.oew || []
                const extraColumns = this.getExtraColumnNames(oewRows)
                const panelKey = this.getAircraftPanelKey(item, index)
                const columns = this.getOewColumns(extraColumns)
                return (
                  <Panel key={panelKey} header={`Aircraft Type: ${item?.type || '-'}`} className='wb-accordion-panel'>
                    <div className='table-responsive wb-type-table-wrap'>
                      <div className='wb-grid-shell'>
                        <Table
                          className='custom-table'
                          columns={columns}
                          dataSource={oewRows}
                          rowKey={(record, rowIndex) => `${record?.id || 'row'}-${rowIndex}`}
                          pagination={false}
                          size='small'
                          locale={{ emptyText: 'No rows available' }}
                        />
                      </div>
                    </div>
                  </Panel>
                )
              })}
            </Collapse>
          </div>
        )}
      </div>
    )
  }
  pax = () => {
    const { data, activeAircraftKeys } = this.state
    const allPanelKeys = this.getAircraftPanelKeys(data)
    const isAllExpanded = allPanelKeys.length > 0 && activeAircraftKeys.length === allPanelKeys.length

    return (
      <div className='wb-panel'>
        <div className='wb-panel-head'>
          <Title level={5} className='mb-0'>Passenger Weight</Title>
          <Space direction='horizontal' size={0} className='ml-auto d-flex'>
            <Upload accept='.xlsx,.xls' showUploadList={false} beforeUpload={(file) => this.handleUpload('/w_b/pax', file)}>
              <Button type='link' className='link-btn-dod uld-upload wb-upload-btn' icon={<UploadOutlined style={{ fontSize: 16 }} />}>Upload</Button>
            </Upload>
            <Button className='custom-button' onClick={this.toggleAllAircraft}>
              {isAllExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </Space>
        </div>

        {data.length === 0 ? (
          <Card className='wb-type-card'>
            <Empty description='No Passenger Weight records found' />
          </Card>
        ) : (
          <div className='wb-card'>
            <Collapse
              activeKey={activeAircraftKeys}
              onChange={this.handleAircraftAccordionChange}
              className='wb-accordion'
              bordered={false}
            >
              {data.map((item, index) => {
                const pax_wts_summer = item.pax_wts_summer || []
                const pax_wts_winter = item.pax_wts_winter || []
                const isArray = Array.isArray(pax_wts_summer) && Array.isArray(pax_wts_winter)
                const panelKey = this.getAircraftPanelKey(item, index)
                const columns = [
                  {
                    title: 'Pax Count',
                    dataIndex: 'pax_count',
                    key: 'pax_count',
                    className: 'wb-col-ship',
                    width: 90,
                    render: (value) => value
                  },
                  {
                    title: 'Cabin 1 Adj Wt',
                    dataIndex: 'cabin_1_adj_wt',
                    key: 'cabin_1_adj_wt',
                    className: 'wb-col-dom',
                    width: 150,
                    render: (value) => value
                  },
                  {
                    title: 'Cabin 2 Adj Wt',
                    dataIndex: 'cabin_2_adj_wt',
                    key: 'cabin_2_adj_wt',
                    className: 'wb-col-intl',
                    width: 150,
                    render: (value) => value
                  },
                  {
                    title: 'Cabin 3 Adj Wt',
                    dataIndex: 'cabin_3_adj_wt',
                    key: 'cabin_3_adj_wt',
                    className: 'wb-col-config',
                    width: 120,
                    render: (value) => value
                  }
                ]
                return (
                  <Panel key={panelKey} header={`Aircraft Type: ${item?.type || '-'}`} className='wb-accordion-panel'>
                    <div className='table-responsive wb-type-table-wrap'>
                      <div className='wb-grid-shell'>
                        <Title level={5} className='mb-0'>Summer</Title>
                        {isArray && (<Table
                          className='custom-table'
                          columns={columns}
                          dataSource={pax_wts_summer}
                          pagination={false}
                          size='small'
                          locale={{ emptyText: 'No rows available' }}
                        />)}
                        {!isArray && Object.keys(pax_wts_summer).map((key) => (
                          <div key={key}>
                            <Title level={5} className='mb-0'>{key?.toUpperCase()?.replaceAll('_', ' ')}</Title>
                            <Table
                              className='custom-table'
                              columns={columns}
                              dataSource={pax_wts_summer[key]}
                              pagination={false}
                              size='small'
                              locale={{ emptyText: 'No rows available' }}
                            />
                          </div>
                        ))}
                      </div>
                      <div className='wb-grid-shell'>
                        <Title level={5} className='mb-0'>Winter</Title>
                        {isArray && (<Table
                          className='custom-table'
                          columns={columns}
                          dataSource={pax_wts_winter}
                          pagination={false}
                          size='small'
                          locale={{ emptyText: 'No rows available' }}
                        />)}
                        {!isArray && Object.keys(pax_wts_winter).map((key) => (
                          <div key={key}>
                            <Title level={5} className='mb-0'>{key?.toUpperCase()?.replaceAll('_', ' ')}</Title>
                            <Table
                              className='custom-table'
                              columns={columns}
                              dataSource={pax_wts_winter[key]}
                              pagination={false}
                              size='small'
                              locale={{ emptyText: 'No rows available' }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </Panel>
                )
              })}
            </Collapse>
          </div>
        )}
      </div>
    )
  }

  getColumns = (object) => {
    let columns = Object.keys(object).map((key) => ({
      title: this.getColumnTitle(key),
      dataIndex: key,
      key: key,
      render: (value) => value
    }))
    columns = columns.filter((col) => col.dataIndex !== 'id')
    return columns
  }
  pallet = () => {
    const { data, activeAircraftKeys } = this.state
    const allPanelKeys = this.getAircraftPanelKeys(data)
    const isAllExpanded = allPanelKeys.length > 0 && activeAircraftKeys.length === allPanelKeys.length

    return (
      <div className='wb-panel'>
        <div className='wb-panel-head'>
          <Title level={5} className='mb-0'>Pallet Weight</Title>
          <Space direction='horizontal' size={0} className='ml-auto d-flex'>
            <Upload accept='.xlsx,.xls' showUploadList={false} beforeUpload={(file) => this.handleUpload('/w_b/pallet', file)}>
              <Button type='link' className='link-btn-dod uld-upload wb-upload-btn' icon={<UploadOutlined style={{ fontSize: 16 }} />}>Upload</Button>
            </Upload>
            <Button className='custom-button' onClick={this.toggleAllAircraft}>
              {isAllExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </Space>
        </div>

        {data.length === 0 ? (
          <Card className='wb-type-card'>
            <Empty description='No Pallet Weight records found' />
          </Card>
        ) : (
          <div className='wb-card'>
            <Collapse
              activeKey={activeAircraftKeys}
              onChange={this.handleAircraftAccordionChange}
              className='wb-accordion'
              bordered={false}
            >
              {data.map((item, index) => {
                const pallet = item.pallet || []
                const isArray = Array.isArray(pallet)
                const panelKey = this.getAircraftPanelKey(item, index)
                return (
                  <Panel key={panelKey} header={`Aircraft Type: ${item?.type || '-'}`} className='wb-accordion-panel'>
                    <div className='table-responsive wb-type-table-wrap'>
                      <div className='wb-grid-shell'>
                        {isArray && (<Table
                          className='custom-table'
                          columns={this.getColumns(pallet[0] || {})}
                          dataSource={pallet}
                          pagination={false}
                          size='small'
                          locale={{ emptyText: 'No rows available' }}
                        />)}
                        {!isArray && Object.keys(pallet).map((key) => (
                          <div key={key}>
                            <Title level={5} className='mb-0'>{key?.toUpperCase()?.replaceAll('_', ' ')}</Title>
                            <Table
                              className='custom-table'
                              columns={this.getColumns(pallet[key][0] || {})}
                              dataSource={pallet[key]}
                              pagination={false}
                              size='small'
                              locale={{ emptyText: 'No rows available' }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </Panel>
                )
              })}
            </Collapse>
          </div>
        )}
      </div>
    )
  }

  cargo = () => {
    const { data, activeAircraftKeys } = this.state
    const allPanelKeys = this.getAircraftPanelKeys(data)
    const isAllExpanded = allPanelKeys.length > 0 && activeAircraftKeys.length === allPanelKeys.length

    return (
      <div className='wb-panel'>
        <div className='wb-panel-head'>
          <Title level={5} className='mb-0'>Cargo Weight</Title>
          <Space direction='horizontal' size={0} className='ml-auto d-flex'>
            <Upload accept='.xlsx,.xls' showUploadList={false} beforeUpload={(file) => this.handleUpload('/w_b/cargo', file)}>
              <Button type='link' className='link-btn-dod uld-upload wb-upload-btn' icon={<UploadOutlined style={{ fontSize: 16 }} />}>Upload</Button>
            </Upload>
            <Button className='custom-button' onClick={this.toggleAllAircraft}>
              {isAllExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </Space>
        </div>

        {data.length === 0 ? (
          <Card className='wb-type-card'>
            <Empty description='No Cargo Weight records found' />
          </Card>
        ) : (
          <div className='wb-card'>
            <Collapse
              activeKey={activeAircraftKeys}
              onChange={this.handleAircraftAccordionChange}
              className='wb-accordion'
              bordered={false}
            >
              {data.map((item, index) => {
                const cargo = item.cargo || []
                const isArray = Array.isArray(cargo)
                const panelKey = this.getAircraftPanelKey(item, index)
                return (
                  <Panel key={panelKey} header={`Aircraft Type: ${item?.type || '-'}`} className='wb-accordion-panel'>
                    <div className='table-responsive wb-type-table-wrap'>
                      <div className='wb-grid-shell'>
                        {isArray && (<Table
                          className='custom-table'
                          columns={this.getColumns(cargo[0] || {})}
                          dataSource={cargo}
                          pagination={false}
                          size='small'
                          locale={{ emptyText: 'No rows available' }}
                        />)}
                        {!isArray && Object.keys(cargo).map((key) => (
                          <div key={key}>
                            <Title level={5} className='mb-0'>{key?.toUpperCase()?.replaceAll('_', ' ')}</Title>
                            <Table
                              className='custom-table'
                              columns={this.getColumns(cargo[key][0] || {})}
                              dataSource={cargo[key]}
                              pagination={false}
                              size='small'
                              locale={{ emptyText: 'No rows available' }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </Panel>
                )
              })}
            </Collapse>
          </div>
        )}
      </div>
    )
  }

  bulk = () => {
    const { data, activeAircraftKeys } = this.state
    const allPanelKeys = this.getAircraftPanelKeys(data)
    const isAllExpanded = allPanelKeys.length > 0 && activeAircraftKeys.length === allPanelKeys.length

    return (
      <div className='wb-panel'>
        <div className='wb-panel-head'>
          <Title level={5} className='mb-0'>Bulk Weight</Title>
          <Space direction='horizontal' size={0} className='ml-auto d-flex'>
            <Upload accept='.xlsx,.xls' showUploadList={false} beforeUpload={(file) => this.handleUpload('/w_b/bulk', file)}>
              <Button type='link' className='link-btn-dod uld-upload wb-upload-btn' icon={<UploadOutlined style={{ fontSize: 16 }} />}>Upload</Button>
            </Upload>
            <Button className='custom-button' onClick={this.toggleAllAircraft}>
              {isAllExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </Space>
        </div>

        {data.length === 0 ? (
          <Card className='wb-type-card'>
            <Empty description='No Bulk Weight records found' />
          </Card>
        ) : (
          <div className='wb-card'>
            <Collapse
              activeKey={activeAircraftKeys}
              onChange={this.handleAircraftAccordionChange}
              className='wb-accordion'
              bordered={false}
            >
              {data.map((item, index) => {
                const bulk = item.bulk || []
                const isArray = Array.isArray(bulk)
                const panelKey = this.getAircraftPanelKey(item, index)
                return (
                  <Panel key={panelKey} header={`Aircraft Type: ${item?.type || '-'}`} className='wb-accordion-panel'>
                    <div className='table-responsive wb-type-table-wrap'>
                      <div className='wb-grid-shell'>
                        {isArray && (<Table
                          className='custom-table'
                          columns={this.getColumns(bulk[0] || {})}
                          dataSource={bulk}
                          pagination={false}
                          size='small'
                          locale={{ emptyText: 'No rows available' }}
                        />)}
                        {!isArray && Object.keys(bulk).map((key) => (
                          <div key={key}>
                            <Title level={5} className='mb-0'>{key?.toUpperCase()?.replaceAll('_', ' ')}</Title>
                            <Table
                              className='custom-table'
                              columns={this.getColumns(bulk[key][0] || {})}
                              dataSource={bulk[key]}
                              pagination={false}
                              size='small'
                              locale={{ emptyText: 'No rows available' }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </Panel>
                )
              })}
            </Collapse>
          </div>
        )}
      </div>
    )
  }
  fuel = () => {
    const { data, activeAircraftKeys } = this.state
    const allPanelKeys = this.getAircraftPanelKeys(data)
    const isAllExpanded = allPanelKeys.length > 0 && activeAircraftKeys.length === allPanelKeys.length

    return (
      <div className='wb-panel'>
        <div className='wb-panel-head'>
          <Title level={5} className='mb-0'>Fuel</Title>
          <Space direction='horizontal' size={0} className='ml-auto d-flex'>
            <Upload accept='.xlsx,.xls' showUploadList={false} beforeUpload={(file) => this.handleUpload('/w_b/fuel', file)}>
              <Button type='link' className='link-btn-dod uld-upload wb-upload-btn' icon={<UploadOutlined style={{ fontSize: 16 }} />}>Upload</Button>
            </Upload>
            <Button className='custom-button' onClick={this.toggleAllAircraft}>
              {isAllExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </Space>
        </div>

        {data.length === 0 ? (
          <Card className='wb-type-card'>
            <Empty description='No Fuel records found' />
          </Card>
        ) : (
          <div className='wb-card'>
            <Collapse
              activeKey={activeAircraftKeys}
              onChange={this.handleAircraftAccordionChange}
              className='wb-accordion'
              bordered={false}
            >
              {data.map((item, index) => {
                const fuel = item.fuel || []
                const isArray = Array.isArray(fuel)
                const panelKey = this.getAircraftPanelKey(item, index)
                return (
                  <Panel key={panelKey} header={`Aircraft Type: ${item?.type || '-'}`} className='wb-accordion-panel'>
                    <div className='table-responsive wb-type-table-wrap'>
                      <div className='wb-grid-shell'>
                        {isArray && (<Table
                          className='custom-table'
                          columns={this.getColumns(fuel[0] || {})}
                          dataSource={fuel}
                          pagination={false}
                          size='small'
                          locale={{ emptyText: 'No rows available' }}
                        />)}
                        {!isArray && Object.keys(fuel).map((key) => (
                          <div key={key}>
                            <Title level={5} className='mb-0'>{key?.toUpperCase()?.replaceAll('_', ' ')}</Title>
                            <Table
                              className='custom-table'
                              columns={this.getColumns(fuel[key][0] || {})}
                              dataSource={fuel[key]}
                              pagination={false}
                              size='small'
                              locale={{ emptyText: 'No rows available' }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </Panel>
                )
              })}
            </Collapse>
          </div>
        )}
      </div>
    )
  }

  env = () => {
    return (
      <div className='wb-panel'>
        <div className='wb-panel-head'>
          <Title level={5} className='mb-0'>Opti Env</Title>
        </div>
        <div className='wb-card'>
          <div className='table-responsive wb-type-table-wrap'>
            <div className='wb-grid-shell'>
              <Table
                className='custom-table'
                columns={this.getColumns(this.state.env?.[0] || {})}
                dataSource={this.state.env || []}
                pagination={false}
                size='small'
                locale={{ emptyText: 'No rows available' }}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  efuel = () => {
    return (
      <div className='wb-panel'>
        <div className='wb-panel-head'>
          <Title level={5} className='mb-0'>Opti Fuel</Title>
        </div>
        <div className='wb-card'>
          <div className='table-responsive wb-type-table-wrap'>
            <div className='wb-grid-shell'>
              <Table
                className='custom-table'
                columns={this.getColumns(this.state.fuel?.[0] || {})}
                dataSource={this.state.fuel || []}
                pagination={false}
                size='small'
                locale={{ emptyText: 'No rows available' }}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  render() {
    // const { data } = this.state
    const settings = {
      dots: false,
      infinite: false,
      speed: 500,
      // slidesToShow: 7,
      slidesToShow: 9,
      slidesToScroll: 1,
      responsive: [
        {
          breakpoint: 1600,
          settings: {
            slidesToShow: 7,
            slidesToScroll: 1,
          }
        },
        {
          breakpoint: 1200,
          settings: {
            slidesToShow: 5,
            slidesToScroll: 2,
          }
        },
        {
          breakpoint: 991,
          settings: {
            slidesToShow: 4,
            slidesToScroll: 4
          }
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 3
          }
        },
        {
          breakpoint: 576,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1
          }
        },
      ]
    }
    return (
      <div>
        <div>
          <Slider {...settings} className='custom-slider wnb-slider'>
            <div className={`tabs-nested ${this.state.tab === 'oew' ? 'active-tab-nested' : ''}`} onClick={() => this.setState({ tab: 'oew' })}>
              <span>OEW</span>
            </div>
            <div className={`tabs-nested ${this.state.tab === 'pax' ? 'active-tab-nested' : ''}`} onClick={() => this.setState({ tab: 'pax' })}>
              <span>PAX</span>
            </div>
            <div className={`tabs-nested ${this.state.tab === 'pallet' ? 'active-tab-nested' : ''}`} onClick={() => this.setState({ tab: 'pallet' })}>
              <span>PALLET</span>
            </div>
            <div className={`tabs-nested ${this.state.tab === 'cargo' ? 'active-tab-nested' : ''}`} onClick={() => this.setState({ tab: 'cargo' })}>
              <span>CARGO</span>
            </div>
            <div className={`tabs-nested ${this.state.tab === 'bulk' ? 'active-tab-nested' : ''}`} onClick={() => this.setState({ tab: 'bulk' })}>
              <span>BULK</span>
            </div>
            <div className={`tabs-nested ${this.state.tab === 'fuel' ? 'active-tab-nested' : ''}`} onClick={() => this.setState({ tab: 'fuel' })}>
              <span>FUEL</span>
            </div>
            <div className={`tabs-nested ${this.state.tab === 'env' ? 'active-tab-nested' : ''}`} onClick={() => this.setState({ tab: 'env' })}>
              <span>OPTI ENV</span>
            </div>
            <div className={`tabs-nested ${this.state.tab === 'efuel' ? 'active-tab-nested' : ''}`} onClick={() => this.setState({ tab: 'efuel' })}>
              <span>ENV FUEL</span>
            </div>
          </Slider>
        </div>
        <div className=''>
          <Card className='custom-card'>
            {this.state.tab === 'oew' && this.oew()}
            {this.state.tab === 'pax' && this.pax()}
            {this.state.tab === 'pallet' && this.pallet()}
            {this.state.tab === 'cargo' && this.cargo()}
            {this.state.tab === 'bulk' && this.bulk()}
            {this.state.tab === 'fuel' && this.fuel()}
            {this.state.tab === 'env' && this.env()}
            {this.state.tab === 'efuel' && this.efuel()}
          </Card>
        </div>
      </div>
    )
  }
}


export default WeightBalance